import * as PIXI from 'pixi.js-legacy';
import isEqual from 'lodash/isEqual';
import { degreeToRadian, toPixel } from '../utils';

export default class PolyMask {
  lines;
  linePositions = { horizontal: [], vertical: [], circle: [] };
  grid;
  lineIndexes;
  padding;
  poly;
  sprite;
  polyMask;
  destroyed = false;
  callback;
  polyOnly;
  onUpdate;
  positions = {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    center: { x: 0, y: 0 },
  };
  maskInfo = {
    x: 0,
    y: 0,
    x2: 0,
    y2: 0,
    height: 0,
    width: 0,
  };

  constructor(
    app,
    grid,
    lines,
    padding = 0,
    polyOnly = false,
    testing = false,
  ) {
    this.grid = grid;
    this.lineIndexes = lines;
    this.app = app;
    this.width = app.screen.width;
    this.height = app.screen.height;
    this.padding = padding;
    this.color = testing ? 0x000000 : 0xffffff;
    this.polyOnly = polyOnly && !this.lineIndexes.circle;

    if (!this.polyOnly) {
      this.poly = new PIXI.Graphics();
      this.poly.interactiveChildren = false;
      this.polyMask = new PIXI.Graphics();
      this.polyMask.interactiveChildren = false;

      this.texture = PIXI.RenderTexture.create({
        width: this.app.screen.width,
        height: this.app.screen.height,
      });
      this.sprite = new PIXI.Sprite(this.texture);
      this.sprite.interactiveChildren = false;
    }

    this.initiateMask();
    this.app.ticker.add(this.ticker, this);
  }

  initiateMask() {
    if (this.lineIndexes.vertical && this.lineIndexes.horizontal) {
      const pointsX = this.lineIndexes.vertical.map(line =>
        this.grid.getEndPosition(
          this.grid.getLineByIndex('vertical', line),
          'vertical',
        ),
      );
      const pointsY = this.lineIndexes.horizontal.map(line =>
        this.grid.getEndPosition(
          this.grid.getLineByIndex('horizontal', line),
          'horizontal',
        ),
      );
      const x = Math.min(...pointsX);
      const y = Math.min(...pointsY);
      const width = Math.abs(pointsX[0] - pointsX[1]);
      const height = Math.abs(pointsY[0] - pointsY[1]);

      const pointsXStart = this.lineIndexes.vertical.map(line =>
        toPixel(
          this.grid.getLineByIndex('vertical', line).startPosition,
          this.grid.width,
        ),
      );
      const pointsYStart = this.lineIndexes.horizontal.map(line =>
        toPixel(
          this.grid.getLineByIndex('horizontal', line).startPosition,
          this.grid.height,
        ),
      );
      const maxWidth = Math.max(
        width,
        Math.abs(pointsXStart[0] - pointsXStart[1]),
      );
      const maxHeight = Math.max(
        height,
        Math.abs(pointsYStart[0] - pointsYStart[1]),
      );

      this.maskInfo = {
        x,
        y,
        width,
        height,
        x2: width + x,
        y2: height + y,
        maxHeight,
        maxWidth,
      };
    }
  }

  setupLines() {
    let vLines = [],
      hLines = [],
      cLines = [];

    if (this.lineIndexes.vertical) {
      vLines = this.lineIndexes.vertical.map(index =>
        this.grid.getLineByIndex('vertical', index, true),
      );
    }

    if (this.lineIndexes.horizontal) {
      hLines = this.lineIndexes.horizontal.map(index =>
        this.grid.getLineByIndex('horizontal', index, true),
      );
    }

    if (this.lineIndexes.circle) {
      cLines = this.lineIndexes.circle.map(index =>
        this.grid.getLineByIndex('circle', index, true),
      );
    }

    this.lines = {
      horizontal: hLines || [],
      vertical: vLines || [],
      circle: cLines || [],
    };

    this.lines.horizontal.forEach((line, index) => {
      const { x: _x, y: _y } = line.getPosition();
      this.linePositions.horizontal[index] = {
        x1: 0,
        y1: _y,
        x2: this.width,
        y2: _y,
      };

      line.setOnChange(({ x, y }) => {
        this.linePositions.horizontal[index] = {
          x1: 0,
          y1: y,
          x2: this.width,
          y2: y,
        };
      });
    });

    this.lines.vertical.forEach((line, index) => {
      const { x: _x, y: _y } = line.getPosition();
      this.linePositions.vertical[index] = {
        x1: _x,
        y1: 0,
        x2: _x,
        y2: this.height,
      };
      line.setOnChange(({ x, y }) => {
        this.linePositions.vertical[index] = {
          x1: x,
          y1: 0,
          x2: x,
          y2: this.height,
        };
      });
    });

    this.lines.circle.forEach((line, index) => {
      const { x: _x, y: _y } = line.getPosition();
      this.linePositions.circle.push({
        x1: _x,
        y1: _y,
        radius: line.lineData.radius,
      });
      line.setOnChange(({ x, y, radius }) => {
        this.linePositions.circle[index] = {
          x1: x,
          y1: y,
          radius,
        };
      });
    });
  }

  removeTicker() {
    this.destroyed = true;
    this.app.ticker.remove(this.ticker, this);
  }

  destroy() {
    this.removeTicker();
    try {
      this.poly.clear();
      this.poly.destroy();
    } catch (e) {}
    try {
      this.sprite.destroy();
    } catch (e) {}
    try {
      this.polyMask.clear();
      this.polyMask.destroy();
    } catch (e) {}
    try {
      this.texture.destroy(true);
    } catch (e) {}
  }

  resetPoly() {
    if (this.polyOnly) return;
    this.poly.clear();
    this.poly.lineStyle(0);
  }

  checkLineIntersection(
    line1StartX,
    line1StartY,
    line1EndX,
    line1EndY,
    line2StartX,
    line2StartY,
    line2EndX,
    line2EndY,
  ) {
    const result = {
      x: null,
      y: null,
      onLine1: false,
      onLine2: false,
    };

    const denominator =
      (line2EndY - line2StartY) * (line1EndX - line1StartX) -
      (line2EndX - line2StartX) * (line1EndY - line1StartY);

    if (denominator === 0) {
      return result;
    }

    let a = line1StartY - line2StartY;
    let b = line1StartX - line2StartX;

    const numerator1 =
      (line2EndX - line2StartX) * a - (line2EndY - line2StartY) * b;
    const numerator2 =
      (line1EndX - line1StartX) * a - (line1EndY - line1StartY) * b;

    a = numerator1 / denominator;
    b = numerator2 / denominator;

    result.x = line1StartX + a * (line1EndX - line1StartX);
    result.y = line1StartY + a * (line1EndY - line1StartY);

    if (a > 0 && a < 1) {
      result.onLine1 = true;
    }
    if (b > 0 && b < 1) {
      result.onLine2 = true;
    }

    return result;
  }

  calculatePolyCenter = points => {
    let totalX = 0,
      totalY = 0;
    const totalPoints = points.length / 2;

    points.forEach((point, index) => {
      if (index % 2 === 0) {
        totalX += point;
      } else {
        totalY += point;
      }
    });

    const centerX = totalX / totalPoints;
    const centerY = totalY / totalPoints;

    return [centerX, centerY];
  };

  getClosedPolygon = (points, reversePadding = false) => {
    const [centerX, centerY] = this.calculatePolyCenter(points);

    const paddedPoints = this.padding
      ? points.map((p, i) => {
          if (i % 2 === 0) {
            if (p > centerX)
              return !reversePadding ? p - this.padding : p + this.padding;
            return !reversePadding ? p + this.padding : p - this.padding;
          } else {
            if (p > centerY)
              return !reversePadding ? p - this.padding : p + this.padding;
            return !reversePadding ? p + this.padding : p - this.padding;
          }
        })
      : points;

    return paddedPoints;
  };

  handleCallback(points) {
    if (this.callback) {
      const xs = [points[0], points[2], points[4], points[6]];
      const ys = [points[1], points[3], points[5], points[7]];
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      const width = Math.abs(Math.max(...xs) - x);
      const height = Math.abs(Math.max(...ys) - y);
      this.callback({ x, y, points, height, width });
    }
  }

  updatePolyMaskStraightLines(lineArray, generateSprite = true) {
    this.resetPoly();

    const points = [];

    for (let i = 0; i < lineArray.length; i++) {
      const nextIndex = (i + 1) % lineArray.length;

      const l1 = lineArray[i];
      const l2 = lineArray[nextIndex];

      const p = this.checkLineIntersection(
        l1.x1,
        l1.y1,
        l1.x2,
        l1.y2,
        l2.x1,
        l2.y1,
        l2.x2,
        l2.y2,
      );

      points.push(p.x, p.y);
    }

    this.handleCallback(points);

    if (this.polyOnly) return;

    this.poly.beginFill(this.color);
    this.poly.drawPolygon(this.getClosedPolygon(points));
    this.poly.endFill();

    if (generateSprite) {
      this.generateSprite();
    }
  }

  updatePolyMaskCircles(circleArray) {
    this.resetPoly();
    this.poly.beginFill(this.color);
    this.poly.drawCircle(
      circleArray[0].x1,
      circleArray[0].y1,
      circleArray[0].radius - this.padding,
    );
    this.poly.endFill();

    if (circleArray[1]) {
      this.poly
        .beginHole()
        .drawCircle(
          circleArray[1].x1,
          circleArray[1].y1,
          circleArray[1].radius - this.padding,
        )
        .endHole();
    }

    if (this.callback) {
      const width = circleArray[0].radius * 2;
      const height = circleArray[0].radius * 2;
      const x = circleArray[0].x1 - circleArray[0].radius;
      const y = circleArray[0].y1 - circleArray[0].radius;
      this.callback({ x, y, height, width });
    }

    this.generateSprite();
  }

  updateMixedPolyMask(circleArray, lineArray) {
    this.polyMask.clear();
    this.polyMask.beginFill(this.color);
    this.polyMask.drawCircle(
      circleArray[0].x1,
      circleArray[0].y1,
      circleArray[0].radius - this.padding,
    );
    this.polyMask.endFill();

    if (circleArray[1]) {
      this.polyMask
        .beginHole()
        .drawCircle(
          circleArray[1].x1,
          circleArray[1].y1,
          circleArray[1].radius - this.padding,
        )
        .endHole();
    }

    this.updatePolyMaskStraightLines(lineArray, false);
    this.poly.mask = this.polyMask;

    if (lineArray[0].x1 > lineArray[0].x2) this.sprite.anchor.x = 1;

    if (lineArray[0].y1 > lineArray[0].y2) {
      this.sprite.anchor.y = 1;
    }

    this.generateSprite();
  }

  generateSprite() {
    this.app.renderer.render(this.poly, this.texture);
  }

  calcPositions(lineCoords) {
    const xVals = [lineCoords[1], lineCoords[3]].map(({ x1, x2 }) =>
      Math.min(x1, x2),
    );
    const yVals = [lineCoords[0], lineCoords[2]].map(({ y1, y2 }) =>
      Math.min(y1, y2),
    );
    const left = Math.min(...xVals);
    const top = Math.min(...yVals);
    const right = Math.max(...xVals);
    const bottom = Math.max(...yVals);
    const center = { x: (right - left) / 2, y: (bottom - top) / 2 };
    this.positions = {
      top,
      left,
      right,
      bottom,
      center,
      width: right - left,
      height: bottom - top,
    };
    if (this.onUpdate) {
      this.onUpdate(this.positions);
    }
  }

  ticker() {
    if (this.destroyed) return;
    try {
      if (!this.lines) {
        this.setupLines();
      } else if (
        this.lines.horizontal.length &&
        this.lines.vertical.length &&
        this.lines.circle.length
      ) {
        const lineCoords = [
          this.linePositions.horizontal[0],
          this.linePositions.vertical[0],
          this.linePositions.horizontal[1],
          this.linePositions.vertical[1],
        ];
        this.calcPositions(lineCoords);
        this.updateMixedPolyMask(this.linePositions.circle, lineCoords);
      } else if (this.lines.horizontal.length && this.lines.vertical.length) {
        const lineCoords = [
          this.linePositions.horizontal[0],
          this.linePositions.vertical[0],
          this.linePositions.horizontal[1],
          this.linePositions.vertical[1],
        ];
        this.calcPositions(lineCoords);
        this.updatePolyMaskStraightLines(lineCoords);
      } else if (this.lines.circle.length) {
        this.updatePolyMaskCircles(this.linePositions.circle);
      }
    } catch (err) {
      this.removeTicker();
      console.log({ error: err, lines: this.lineIndexes });
      console.error(err);
    }
  }
}
